import { MediumOptions } from 'modules/messagingInsights/constants';
import { useState } from 'react';
import styled from 'styled-components';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { InputSearch } from 'ui/inputs';
import { Tab, TabGroup } from 'ui/tabs';
import { Close16, Help } from 'ui/icons';
import { Toggle, TOGGLE_SIZES } from 'ui/toggle';
import { body2, colors, spacing } from 'ui/theme';
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from 'ui/workflows/drawer/Drawer';
import { pxToRem } from 'utils/convert';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';
import {
  lastUpdated,
  metricDefinitions,
  metricDefinitionsByMedium,
} from '../assets/metricDefinitions';
import { Formula } from './Formula';

const StyledDrawer = styled(Drawer)<{ navBarHeight: number }>`
  ${body2}
  height: calc(100vh - ${({ navBarHeight }) => `${navBarHeight}px`});
  position: fixed;
  right: 0;
  top: ${({ navBarHeight }) => `${navBarHeight}px`};
  width: ${pxToRem(525)};
  z-index: 11;
`;

const Title = styled.h3`
  ${body2}
  margin: 0;
  padding-left: ${spacing.half};
  font-size: ${spacing.plusHalf};
`;

const SearchWrapper = styled.div`
  margin: ${spacing.plusHalf} 0;
`;

const MetricContainer = styled.div`
  margin-bottom: ${spacing.double};
`;

const Label = styled.div`
  font-size: ${pxToRem(20)};
  font-weight: bold;
  margin-bottom: ${spacing.half};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
`;

const ToggleName = styled.div`
  margin-right: ${spacing.quarter};
`;

const StyledButton = styled(IconButton)`
  margin-bottom: ${spacing.eighth};
`;

export const MetricDefinitionDrawer = () => {
  const [search, setSearch] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedTab, setSelectedTab] = useState('Email');
  const [codeView, setCodeView] = useState(false);
  const navBarHeight = useNavBarHeight();

  const filteredMetrics = Object.keys(metricDefinitionsByMedium[selectedTab]).filter(
    (metric) => metric.toLowerCase().indexOf(search.toLowerCase()) !== -1,
  );

  return (
    <>
      {!showDrawer && (
        <StyledButton onClick={() => setShowDrawer(true)} variant={BUTTON_VARIANTS.GHOST}>
          <Help height={16} width={16} color={colors.green100} />
        </StyledButton>
      )}
      {showDrawer && (
        <StyledDrawer navBarHeight={navBarHeight}>
          <DrawerHeader>
            <Title>Metric Definitions</Title>
            <HeaderActions>
              <ToggleName>Code view</ToggleName>
              <Toggle
                isActive={codeView}
                onClick={(isActive) => setCodeView(isActive as boolean)}
                size={TOGGLE_SIZES.SMALL}
              ></Toggle>
              <IconButton onClick={() => setShowDrawer(false)} variant={BUTTON_VARIANTS.GHOST}>
                <Close16 />
              </IconButton>
            </HeaderActions>
          </DrawerHeader>
          <DrawerBody>
            <TabGroup selectedTab={selectedTab} onTabChange={(id) => setSelectedTab(id)}>
              {[...Object.keys(MediumOptions), 'Other'].map((medium) => (
                <Tab key={medium} id={medium}>
                  {MediumOptions[medium] || 'Other'}
                </Tab>
              ))}
            </TabGroup>
            <SearchWrapper>
              <InputSearch
                id="metric-search"
                placeholder={'Find metric'}
                onChange={setSearch}
                value={search}
              />
            </SearchWrapper>

            {filteredMetrics.map((key) => {
              const metric = metricDefinitions[key];
              return (
                <MetricContainer key={key}>
                  <Label>{metric?.label}</Label>
                  {metric?.description && <p>{metric.description}</p>}
                  {metric?.numerator && (
                    <Formula
                      numerator={metric.numerator}
                      denominator={metric.denominator}
                      codeView={codeView}
                    />
                  )}
                </MetricContainer>
              );
            })}
          </DrawerBody>
          <DrawerFooter>Last updated: {lastUpdated}</DrawerFooter>
        </StyledDrawer>
      )}
    </>
  );
};
